/**
 * Canonical Form Field Contract
 *
 * Task: T-P5-D4-F2-1
 * Feature: P5-D4-F2 Shared Form Contract Upgrade
 *
 * Single source of truth for form field Zod schemas and inferred types.
 * Both API and webapp import from this module via @telify/shared.
 *
 * Field types: TEXT, EMAIL, NUMBER, TEXTAREA, SELECT, CHECKBOX, RADIO, DATE, RATING, SIGNATURE
 */

import { z } from 'zod';

// ============================================================================
// Constants
// ============================================================================

export const SHOW_WHEN_OPERATORS = ['equals', 'notEquals', 'contains', 'isNotEmpty'] as const;

// ============================================================================
// Schemas
// ============================================================================

/**
 * Field types — 11 total (8 original + RATING, SIGNATURE from D4, CONTENT from D4-F9)
 */
export const fieldTypeEnum = z.enum([
  'TEXT',
  'EMAIL',
  'NUMBER',
  'TEXTAREA',
  'SELECT',
  'CHECKBOX',
  'RADIO',
  'DATE',
  'RATING',
  'SIGNATURE',
  'CONTENT',
]);

/**
 * Option schema for SELECT, RADIO, and multi-select CHECKBOX fields
 */
export const formOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

/**
 * showWhen conditional visibility rule.
 * Single-condition: one source field, one operator, optional value.
 */
export const showWhenSchema = z.object({
  fieldId: z.string(),
  operator: z.enum(SHOW_WHEN_OPERATORS),
  value: z.string().optional(),
});

/**
 * Tag rule — maps a form option value to a tag assignment.
 * v1: assign only. Execution logic is in F4, schema pass-through here.
 */
export const tagRuleSchema = z.object({
  optionValue: z.string(),
  tagId: z.string(),
  action: z.enum(['assign']),
});

/**
 * Canonical form field schema.
 *
 * CHECKBOX field behavior:
 * - If `options` is present: multi-select checkbox group (value is string[])
 * - If `options` is absent: single consent-style checkbox (value is boolean)
 *
 * `required` uses .default(false) — NOT .optional().default(false).
 * This resolves the drift between the former backend and webapp schemas.
 */
export const formFieldSchema = z.object({
  // Core
  fieldId: z.string(),
  type: fieldTypeEnum,
  label: z.string(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  required: z.boolean().default(false),
  options: z.array(formOptionSchema).optional(),
  // form-embed D13: how a RADIO or SELECT question shows wherever the form is filled in; absent is a list.
  display: z.enum(['list', 'chips']).optional(),

  // Validation constraints
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  patternMessage: z.string().optional(),

  // Privacy
  isPII: z.boolean().default(false),

  // Conditional Logic (v1)
  showWhen: showWhenSchema.optional(),

  // Tag Rules (schema only — execution in F4)
  tagRules: z.array(tagRuleSchema).optional(),

  // Rating-specific
  maxStars: z.number().optional(),

  // Linked group (builder-only unit metadata, form-builder-redesign D32): adjacent fields sharing
  // a groupId are drawn and moved as one card on the admin canvas. Clients see ordinary fields.
  groupId: z.string().optional(),

  // Set by the builder quick-add, survives label edits, dropped when the field type changes,
  // and read by the Results field rule.
  preset: z.enum(['phone', 'address', 'consent', 'referral']).optional(),
});

/**
 * Form response input schema.
 * Value union covers all field types:
 * - string: TEXT, EMAIL, TEXTAREA, DATE, SELECT, RADIO, SIGNATURE
 * - boolean: consent CHECKBOX
 * - string[]: multi-select CHECKBOX
 * - number: NUMBER, RATING
 */
export const formResponseInputSchema = z.object({
  fieldId: z.string(),
  value: z.union([
    z.string(),
    z.boolean(),
    z.array(z.string()),
    z.number(),
  ]),
});

// ============================================================================
// Inferred Types
// ============================================================================

export type FieldType = z.infer<typeof fieldTypeEnum>;
export type FormOption = z.infer<typeof formOptionSchema>;
export type ShowWhen = z.infer<typeof showWhenSchema>;
export type ShowWhenOperator = typeof SHOW_WHEN_OPERATORS[number];
export type TagRule = z.infer<typeof tagRuleSchema>;
export type FormField = z.infer<typeof formFieldSchema>;
export type FormResponseInput = z.infer<typeof formResponseInputSchema>;
